import { SamplePack } from '@arpeggi/shared'
import moment from 'moment'
import { useEffect } from 'react'
import { useImmer } from 'use-immer'
import { saleIsExpired, saleIsLive } from '../utils'

const DEFAULT_STATE = {
  mintHasExpired: true,
  activationText: '',
  subtitle: '',
  countdownDate: undefined
}

const getActivationText = (sp: SamplePack, isPresale: boolean): string => {
  if (!saleIsLive(sp, isPresale) && !saleIsExpired(sp, isPresale)) {
    return null
  }

  const preSaleEndDateReadable = moment(sp?.edition?.preSaleEndTime).format('MMMM Do, YYYY')
  const preSaleEndTimeReadable = moment(sp?.edition?.preSaleEndTime).format('hh:mma')

  const saleEndDateReadable = moment(sp?.edition?.publicSaleEndTime).format('MMMM Do, YYYY')
  const saleEndTimeReadable = moment(sp?.edition?.publicSaleEndTime).format('hh:mma')

  const dateReadable = isPresale ? preSaleEndDateReadable : saleEndDateReadable
  const timeReadable = isPresale ? preSaleEndTimeReadable : saleEndTimeReadable

  const prefix = isPresale ? 'Presale' : 'Collection'

  return `${prefix} ${
    saleIsExpired(sp, isPresale) ? 'ended' : 'ends'
  } ${dateReadable} at ${timeReadable}`
}

const getSubtitle = (sp: SamplePack, isPresale: boolean): string => {
  const saleLive = saleIsLive(sp, isPresale)
  const saleExpired = saleIsExpired(sp, isPresale)

  if (saleLive) {
    return 'Live'
  }

  const preface = saleExpired ? 'Ended' : 'Begins in'
  const endDate = isPresale ? sp?.edition?.preSaleEndTime : sp?.edition?.publicSaleEndTime
  const startDate = isPresale ? sp?.edition?.preSaleStartTime : sp?.edition?.publicSaleStartTime

  let prettyDate: string
  if (saleExpired) {
    prettyDate = moment(endDate).format('MMM D')
  } else {
    const now = moment()
    const diff = moment.duration(moment(startDate).diff(now))
    prettyDate = `${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`
  }
  return `${preface} ${prettyDate}`
}

const getCountdownDate = (sp: SamplePack, isPresale: boolean): string => {
  if (saleIsLive(sp, isPresale)) {
    return isPresale ? sp?.edition!.preSaleEndTime : sp?.edition!.publicSaleEndTime
  }
  return isPresale ? sp?.edition!.preSaleStartTime : sp?.edition!.publicSaleStartTime
}

interface State {
  mintHasExpired: boolean
  activationText: string
  subtitle: string
  countdownDate: string
}

export const usePriceCardState = (sp: SamplePack, isPresale: boolean) => {
  const [state, setState] = useImmer<State>(DEFAULT_STATE)

  const updateVariables = () => {
    setState((draft) => {
      draft.mintHasExpired = saleIsExpired(sp, isPresale)
      draft.activationText = getActivationText(sp, isPresale)
      draft.subtitle = getSubtitle(sp, isPresale)
      draft.countdownDate = getCountdownDate(sp, isPresale)
    })
  }

  useEffect(() => {
    updateVariables()
    const interval = setInterval(() => updateVariables(), 1000)
    return () => clearInterval(interval)
  }, [sp, isPresale, updateVariables])

  return state
}
